import type { ThemeOptions } from '@mui/material/styles'

import type { ThemeType } from './types'

// Slate-tinted ramp (xs→2xl) so depth reads cool, not muddy
const lightShadows: ThemeOptions['shadows'] = [
  'none',
  // Level 1: xs — cards at rest
  '0 1px 2px 0 rgba(15, 23, 42, 0.05)',
  // Level 2: sm
  '0 1px 3px 0 rgba(15, 23, 42, 0.09), 0 1px 2px -1px rgba(15, 23, 42, 0.08)',
  // Level 3: md — menus/popovers, hover-lifted cards
  '0 4px 8px -2px rgba(15, 23, 42, 0.10), 0 2px 4px -2px rgba(15, 23, 42, 0.08)',
  // Level 4: lg
  '0 10px 18px -4px rgba(15, 23, 42, 0.12), 0 4px 8px -4px rgba(15, 23, 42, 0.10)',
  // Level 5: xl — dialogs, quick-access sidebar
  '0 20px 30px -8px rgba(15, 23, 42, 0.16), 0 8px 12px -6px rgba(15, 23, 42, 0.12)',
  // Level 6: 2xl
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  // Level 7
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  // Level 8: Dropdown/popover shadow
  '0 4px 8px -2px rgba(15, 23, 42, 0.10), 0 2px 4px -2px rgba(15, 23, 42, 0.08)',
  // Levels 9-24: Repeat large shadows for compatibility
  '0 20px 30px -8px rgba(15, 23, 42, 0.16), 0 8px 12px -6px rgba(15, 23, 42, 0.12)',
  '0 20px 30px -8px rgba(15, 23, 42, 0.16), 0 8px 12px -6px rgba(15, 23, 42, 0.12)',
  '0 20px 30px -8px rgba(15, 23, 42, 0.16), 0 8px 12px -6px rgba(15, 23, 42, 0.12)',
  '0 20px 30px -8px rgba(15, 23, 42, 0.16), 0 8px 12px -6px rgba(15, 23, 42, 0.12)',
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
  '0 28px 56px -12px rgba(15, 23, 42, 0.24)',
]

// Deeper ramp so elevation still reads on navy surfaces
const darkShadows: ThemeOptions['shadows'] = [
  'none',
  // Level 1: Very subtle
  '0 1px 2px 0 rgba(0, 0, 0, 0.30)',
  // Level 2: Subtle
  '0 1px 3px 0 rgba(0, 0, 0, 0.42), 0 1px 2px -1px rgba(0, 0, 0, 0.40)',
  // Level 3: Small
  '0 4px 10px -2px rgba(0, 0, 0, 0.46), 0 2px 4px -2px rgba(0, 0, 0, 0.40)',
  // Level 4: Medium
  '0 12px 20px -4px rgba(0, 0, 0, 0.50), 0 4px 8px -4px rgba(0, 0, 0, 0.45)',
  // Level 5: Large
  '0 22px 34px -8px rgba(0, 0, 0, 0.55), 0 8px 14px -6px rgba(0, 0, 0, 0.50)',
  // Level 6: Extra large
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  // Level 7
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  // Level 8: Dropdown/popover shadow
  '0 4px 10px -2px rgba(0, 0, 0, 0.46), 0 2px 4px -2px rgba(0, 0, 0, 0.40)',
  // Levels 9-24: Repeat large shadows for compatibility
  '0 22px 34px -8px rgba(0, 0, 0, 0.55), 0 8px 14px -6px rgba(0, 0, 0, 0.50)',
  '0 22px 34px -8px rgba(0, 0, 0, 0.55), 0 8px 14px -6px rgba(0, 0, 0, 0.50)',
  '0 22px 34px -8px rgba(0, 0, 0, 0.55), 0 8px 14px -6px rgba(0, 0, 0, 0.50)',
  '0 22px 34px -8px rgba(0, 0, 0, 0.55), 0 8px 14px -6px rgba(0, 0, 0, 0.50)',
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
  '0 28px 56px -12px rgba(0, 0, 0, 0.66)',
]

export const shadows = (theme: ThemeType): ThemeOptions['shadows'] =>
  theme === 'dark' ? darkShadows : lightShadows
